import { z } from 'zod';
import { createResponseSchema } from '../pagination';
import { Task } from './base';

// Velocity tracking enums
export enum VelocityMetricTypeEnum {
  STORY_POINTS = 'STORY_POINTS',
  TASK_COUNT = 'TASK_COUNT',
  HOURS = 'HOURS',
}

export enum VelocityCalculationMethodEnum {
  COMPLETED_ONLY = 'COMPLETED_ONLY',
  COMMITTED_VS_COMPLETED = 'COMMITTED_VS_COMPLETED',
  WEIGHTED_AVERAGE = 'WEIGHTED_AVERAGE',
}

// Velocity calculation schema
export const VelocityCalculationSchema = z.object({
  sprintId: z.string().uuid(),
  sprintName: z.string(),
  startDate: z.date(),
  endDate: z.date(),
  plannedVelocity: z.number().min(0).default(0), // Story points planned
  completedVelocity: z.number().min(0).default(0), // Story points completed
  taskCount: z.number().min(0).default(0), // Number of tasks completed
  completionPercentage: z.number().min(0).max(100).default(0), // Percentage of planned work completed
  averageTaskSize: z.number().min(0).default(0), // Average story points per task
  velocityEfficiency: z.number().min(0).default(0), // Completed vs planned velocity ratio
});

export type VelocityCalculation = z.infer<typeof VelocityCalculationSchema>;

// Team velocity metrics schema
export const TeamVelocityMetricsSchema = z.object({
  teamId: z.string().uuid().optional(),
  organizationId: z.string().uuid(),
  sprintCount: z.number().min(1), // Number of sprints analyzed
  averageVelocity: z.number().min(0).default(0), // Average story points per sprint
  velocityTrend: z.number().default(0), // Positive = increasing, negative = decreasing
  velocityStability: z.number().min(0).max(100).default(0), // How consistent velocity is (0-100%)
  lastSprintVelocity: z.number().min(0).default(0),
  bestSprintVelocity: z.number().min(0).default(0),
  worstSprintVelocity: z.number().min(0).default(0),
  predictedNextVelocity: z.number().min(0).default(0), // Predicted velocity for next sprint
  confidenceScore: z.number().min(0).max(100).default(0), // Confidence in prediction (0-100%)
  sprintHistory: z.array(VelocityCalculationSchema).default([]),
  calculatedAt: z.date().default(new Date()),
});

export type TeamVelocityMetrics = z.infer<typeof TeamVelocityMetricsSchema>;

// Velocity forecast schema
export const VelocityForecastSchema = z.object({
  organizationId: z.string().uuid(),
  teamId: z.string().uuid().optional(),
  forecastPeriods: z.number().min(1).max(10).default(3), // Number of sprints to forecast
  basedOnSprints: z.number().min(1).default(3), // Number of past sprints to base forecast on
  forecasts: z.array(z.object({
    sprintNumber: z.number(),
    predictedVelocity: z.number().min(0),
    optimisticVelocity: z.number().min(0),
    pessimisticVelocity: z.number().min(0),
    confidenceLevel: z.number().min(0).max(100),
  })),
  assumptions: z.object({
    teamSizeStable: z.boolean().default(true),
    workComplexitySimilar: z.boolean().default(true),
    externalFactorsStable: z.boolean().default(true),
  }),
  generatedAt: z.date().default(new Date()),
});

export type VelocityForecast = z.infer<typeof VelocityForecastSchema>;

// Sprint velocity report schema
export const SprintVelocityReportSchema = z.object({
  sprintId: z.string().uuid(),
  organizationId: z.string().uuid(),
  sprintName: z.string(),
  startDate: z.date(),
  endDate: z.date(),
  status: z.enum(['PLANNED', 'ACTIVE', 'COMPLETED', 'CANCELLED']),
  
  // Velocity metrics
  plannedStoryPoints: z.number().min(0).default(0),
  completedStoryPoints: z.number().min(0).default(0),
  addedStoryPoints: z.number().min(0).default(0), // Points added during sprint
  removedStoryPoints: z.number().min(0).default(0), // Points removed during sprint
  
  // Task metrics
  plannedTaskCount: z.number().min(0).default(0),
  completedTaskCount: z.number().min(0).default(0),
  carryOverTaskCount: z.number().min(0).default(0), // Tasks not completed
  
  // Performance indicators
  velocityAchievement: z.number().min(0).default(0), // Percentage of planned velocity achieved
  scopeChange: z.number().default(0), // Net change in scope during sprint
  burndownEfficiency: z.number().min(0).max(100).default(0), // How well the team followed burndown
  
  // Breakdown by priority
  velocityByPriority: z.object({
    urgent: z.number().min(0).default(0),
    high: z.number().min(0).default(0),
    medium: z.number().min(0).default(0),
    low: z.number().min(0).default(0),
  }),
  
  // Breakdown by assignee
  velocityByAssignee: z.array(z.object({
    userId: z.string().uuid(),
    userName: z.string(),
    completedStoryPoints: z.number().min(0),
    completedTaskCount: z.number().min(0),
    efficiency: z.number().min(0).max(100),
  })).default([]),
  
  // Quality metrics
  taskCompletionRate: z.number().min(0).max(100).default(0),
  averageTaskCycleTime: z.number().min(0).default(0), // Average time from start to completion
  defectRate: z.number().min(0).default(0), // Tasks that needed rework
  
  generatedAt: z.date().default(new Date()),
});

export type SprintVelocityReport = z.infer<typeof SprintVelocityReportSchema>;

// Velocity comparison schema
export const VelocityComparisonSchema = z.object({
  organizationId: z.string().uuid(),
  teamId: z.string().uuid().optional(),
  comparisonPeriod: z.enum(['LAST_3_SPRINTS', 'LAST_6_SPRINTS', 'LAST_QUARTER', 'LAST_YEAR']),
  baselineVelocity: z.number().min(0),
  currentVelocity: z.number().min(0),
  velocityChange: z.number(), // Positive = improvement, negative = decline
  velocityChangePercentage: z.number(),
  trend: z.enum(['IMPROVING', 'STABLE', 'DECLINING']),
  factors: z.array(z.object({
    factor: z.string(),
    impact: z.enum(['POSITIVE', 'NEGATIVE', 'NEUTRAL']),
    description: z.string(),
  })).default([]),
  recommendations: z.array(z.string()).default([]),
  comparedAt: z.date().default(new Date()),
});

export type VelocityComparison = z.infer<typeof VelocityComparisonSchema>;

// Velocity-related response schemas using factories
export const VelocityCalculationResponseSchema = createResponseSchema(VelocityCalculationSchema);
export const TeamVelocityMetricsResponseSchema = createResponseSchema(TeamVelocityMetricsSchema);
export const SprintVelocityReportResponseSchema = createResponseSchema(SprintVelocityReportSchema);
export const VelocityForecastResponseSchema = createResponseSchema(VelocityForecastSchema);
export const VelocityComparisonResponseSchema = createResponseSchema(VelocityComparisonSchema);

export type VelocityCalculationResponse = z.infer<typeof VelocityCalculationResponseSchema>;
export type TeamVelocityMetricsResponse = z.infer<typeof TeamVelocityMetricsResponseSchema>;
export type SprintVelocityReportResponse = z.infer<typeof SprintVelocityReportResponseSchema>;
export type VelocityForecastResponse = z.infer<typeof VelocityForecastResponseSchema>;
export type VelocityComparisonResponse = z.infer<typeof VelocityComparisonResponseSchema>;

// Velocity tracking utility types
export interface VelocityTrackingTask extends Task {
  startedAt?: Date | null;
  completedAt?: Date | null;
  estimateHistory?: Array<{
    estimate: number;
    changedAt: Date;
    changedBy: string;
    reason?: string;
  }>;
  blockageHistory?: Array<{
    blockedAt: Date;
    unblockedAt?: Date;
    reason: string;
    blockedBy: string;
  }>;
}

export interface SprintVelocityData {
  sprintId: string;
  sprintName: string;
  startDate: Date;
  endDate: Date;
  isActive: boolean;
  isCompleted: boolean;
  
  // Velocity metrics
  plannedVelocity: number;
  completedVelocity: number;
  velocityAchievement: number; // Percentage
  
  // Task breakdown
  totalTasks: number;
  completedTasks: number;
  carryOverTasks: number;
  addedTasks: number;
  
  // Team performance
  teamEfficiency: number; // Percentage
  averageTaskCycleTime: number; // Hours
  blockedTimePercentage: number; // Percentage of sprint time tasks were blocked
}

export interface VelocityTrendData {
  sprintNumber: number;
  sprintName: string;
  velocity: number;
  trend: 'up' | 'down' | 'stable';
  trendPercentage: number;
  date: Date;
}

export interface TeamPerformanceMetrics {
  teamId?: string;
  organizationId: string;
  period: {
    startDate: Date;
    endDate: Date;
  };
  
  // Overall metrics
  totalVelocity: number;
  averageVelocity: number;
  velocityVariability: number; // Standard deviation
  velocityTrend: 'improving' | 'stable' | 'declining';
  
  // Quality metrics
  estimateAccuracy: number; // Percentage
  onTimeDelivery: number; // Percentage
  reworkRate: number; // Percentage
  
  // Individual performance
  teamMembers: Array<{
    userId: string;
    userName: string;
    velocityContribution: number;
    taskCompletionRate: number;
    averageTaskSize: number;
    estimateAccuracy: number;
  }>;
}

// Velocity calculation utility functions
export class VelocityCalculator {
  /**
   * Calculate velocity for a sprint based on completed tasks
   */
  static calculateSprintVelocity(tasks: Task[]): number {
    return tasks
      .filter(task => task.completedAt && task.storyPoints)
      .reduce((total, task) => total + (task.storyPoints || 0), 0);
  }

  /**
   * Calculate average velocity over multiple sprints
   */
  static calculateAverageVelocity(velocityData: VelocityCalculation[]): number {
    if (velocityData.length === 0) return 0;
    const totalVelocity = velocityData.reduce((sum, data) => sum + data.completedVelocity, 0);
    return totalVelocity / velocityData.length;
  }

  /**
   * Calculate velocity trend (positive = improving, negative = declining)
   */
  static calculateVelocityTrend(velocityData: VelocityCalculation[]): number {
    if (velocityData.length < 2) return 0;
    
    // Sort by date to ensure chronological order
    const sortedData = velocityData.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    
    // Simple linear regression to calculate trend
    const n = sortedData.length;
    const sumX = (n * (n - 1)) / 2; // Sum of indices 0, 1, 2, ..., n-1
    const sumY = sortedData.reduce((sum, data) => sum + data.completedVelocity, 0);
    const sumXY = sortedData.reduce((sum, data, index) => sum + (index * data.completedVelocity), 0);
    const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6; // Sum of squares of indices
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    return slope;
  }

  /**
   * Calculate velocity stability (0-100%, higher = more consistent)
   */
  static calculateVelocityStability(velocityData: VelocityCalculation[]): number {
    if (velocityData.length < 2) return 100;
    
    const velocities = velocityData.map(data => data.completedVelocity);
    const average = velocities.reduce((sum, v) => sum + v, 0) / velocities.length;
    
    if (average === 0) return 100;
    
    const variance = velocities.reduce((sum, v) => sum + Math.pow(v - average, 2), 0) / velocities.length;
    const standardDeviation = Math.sqrt(variance);
    const coefficientOfVariation = standardDeviation / average;
    
    // Convert to stability percentage (inverse of coefficient of variation)
    return Math.max(0, Math.min(100, (1 - coefficientOfVariation) * 100));
  }

  /**
   * Predict next sprint velocity based on historical data
   */
  static predictNextVelocity(velocityData: VelocityCalculation[]): { prediction: number; confidence: number } {
    if (velocityData.length === 0) return { prediction: 0, confidence: 0 };
    if (velocityData.length === 1) {
      const [single] = velocityData;
      return { prediction: single?.completedVelocity ?? 0, confidence: 50 };
    }
    
    const trend = this.calculateVelocityTrend(velocityData);
    const averageVelocity = this.calculateAverageVelocity(velocityData);
    const stability = this.calculateVelocityStability(velocityData);
    
    // Prediction is average velocity plus trend adjustment
    const prediction = Math.max(0, averageVelocity + trend);
    
    // Confidence based on stability and data points
    const dataPointsFactor = Math.min(100, (velocityData.length / 5) * 100); // Max confidence at 5+ sprints
    const confidence = (stability * 0.7) + (dataPointsFactor * 0.3);
    
    return { prediction, confidence };
  }

  /**
   * Calculate estimate accuracy for a task
   */
  static calculateEstimateAccuracy(originalEstimate: number, finalEstimate: number): number {
    if (originalEstimate === 0) return 0;
    const accuracy = 100 - Math.abs((originalEstimate - finalEstimate) / originalEstimate) * 100;
    return Math.max(0, Math.min(100, accuracy));
  }

  /**
   * Calculate task cycle time in hours
   */
  static calculateCycleTime(startDate: Date, endDate: Date): number {
    return Math.abs(endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
  }
} 
